import {
  GoogleOAuthProvider,
  GoogleLogin,
  useGoogleOneTapLogin,
  CredentialResponse,
} from "@react-oauth/google";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { userApiClient, ApiException } from "@/lib/userApiClient";

interface GoogleOAuthButtonProps {
  /**
   * 登录成功回调
   */
  onSuccess?: (response: any) => void;

  /**
   * 登录开始回调
   */
  onLoginStart?: () => void;

  /**
   * 登录失败回调
   */
  onError?: (error: Error) => void;

  /**
   * 用户所在地区
   */
  region?: "cn" | "global";

  /**
   * 按钮样式
   */
  variant?: "default" | "outline";

  /**
   * 按钮大小
   */
  size?: "default" | "sm" | "lg";

  /**
   * 邀请 token
   */
  inviteToken?: string | null;

  /**
   * 模式：login（登录/注册）或 bind（绑定账号）
   */
  mode?: "login" | "bind";

  /**
   * 登录成功后重定向路径
   */
  redirectTo?: string;
}

/**
 * 内部组件：使用 Hooks 处理登录逻辑
 */
function InnerGoogleOAuthButton({
  region = "global",
  inviteToken,
  mode = "login",
  redirectTo = "/",
  onSuccess,
  onLoginStart,
  onError,
}: GoogleOAuthButtonProps) {
  const { t } = useTranslation("login");
  const [isProcessing, setIsProcessing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<string>("300"); // Initial width as a fallback

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Google button width range: 200px - 400px
        const newWidth = Math.max(
          200,
          Math.min(containerRef.current.offsetWidth, 400),
        );
        setWidth(newWidth.toString());
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleCredentialResponse = async (
    credentialResponse: CredentialResponse,
  ) => {
    if (isProcessing) return;

    if (!credentialResponse.credential) {
      toast.error(t("common.networkError"));
      return;
    }

    onLoginStart?.();
    setIsProcessing(true);

    try {
      const idToken = credentialResponse.credential;

      // 根据模式选择 API 端点
      const endpoint = mode === "bind" ? "/security/google" : "/auth/google";

      const payload: any = {
        id_token: idToken,
        country_region: region, // 虽然 Google 提供了区域信息，但在 API 中明确传递
        signup_channel: "google",
      };

      if (inviteToken) {
        payload.invite_token = inviteToken;
      }

      const response = await userApiClient.post(endpoint, payload);

      toast.success(t("common.loginSuccess"));

      // 触发回调
      if (onSuccess) {
        onSuccess(response);
      }

      // 仅在登录模式下自动重定向
      if (mode === "login") {
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 500);
        // keep processing true to prevent clicks during redirect
      } else {
        setIsProcessing(false);
      }
    } catch (error) {
      setIsProcessing(false);
      console.error("Google Login Error:", error);
      if (onError && error instanceof Error) {
        onError(error);
      }

      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("common.networkError"));
      }
    }
  };

  const handleLoginError = () => {
    toast.error(t("common.networkError"));
    if (onError) {
      onError(new Error("Google Login Failed"));
    }
  };

  // 启用 One Tap (仅 LOGIN 模式)
  useGoogleOneTapLogin({
    onSuccess: handleCredentialResponse,
    onError: handleLoginError,
    disabled: mode !== "login" || isProcessing,
    auto_select: false, // 避免自动选定骚扰用户，由用户主动点击
  });

  return (
    <div
      ref={containerRef}
      className={`w-full flex justify-center ${isProcessing ? "opacity-70 pointer-events-none" : ""}`}
    >
      <GoogleLogin
        key={width}
        onSuccess={handleCredentialResponse}
        onError={handleLoginError}
        useOneTap={false} // OneTap 单独控制
        theme="outline"
        size="large"
        shape="rectangular"
        width={width}
        text={mode === "bind" ? "signin_with" : "continue_with"}
      />
    </div>
  );
}

/**
 * Google OAuth 登录按钮组件
 * 使用 @react-oauth/google (GIS SDK) 实现
 * 支持 "Sign In With Google" 按钮和 One Tap 体验
 */
export default function GoogleOAuthButton(props: GoogleOAuthButtonProps) {
  const { i18n } = useTranslation();
  // 优先从 meta 标签读取(运行时)，回退到 import.meta.env(构建时)
  const metaClientId = document
    .querySelector('meta[name="google-client-id"]')
    ?.getAttribute("content");
  const envClientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID;
  const clientId = metaClientId || envClientId;

  if (!clientId) {
    console.warn("Google Client ID not configured (VITE_GOOGLE_CLIENT_ID)");
    return null;
  }

  // Google locale handling: use 'zh-CN' for Chinese, 'en' for others as a safe default
  // Google supports BCP 47 tags, so i18n.language (e.g. 'zh-CN', 'en-US') should generally work.
  // Passing locale to GoogleOAuthProvider ensures the GSI script is loaded with the correct hl param.
  // We use key to force re-mounting and script reloading when language changes.
  return (
    <GoogleOAuthProvider
      clientId={clientId}
      locale={i18n.language}
      key={`${clientId}-${i18n.language}`}
    >
      <InnerGoogleOAuthButton {...props} />
    </GoogleOAuthProvider>
  );
}
